import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import "@/App.css";

// Marketplace Pages
import { AuthProvider } from "@/contexts/AuthContext";
import { SidebarProvider } from "@/contexts/SidebarContext";
import { Toaster } from "@/components/ui/sonner";
import MobileBottomNav from "@/components/marketplace/MobileBottomNav";
import PushAutoSubscribe from "@/components/marketplace/PushAutoSubscribe";

// Eagerly load the login/register (entry points) and dashboard (most visited)
import MarketplaceLogin from "@/pages/marketplace/Login";
import MarketplaceRegister from "@/pages/marketplace/Register";
import MarketplaceDashboard from "@/pages/marketplace/Dashboard";

// Lazy-load everything else
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const EventBriefForm = lazy(() => import("@/pages/EventBriefForm"));
const DesignGenerator = lazy(() => import("@/pages/DesignGenerator"));
const DesignViewer = lazy(() => import("@/pages/DesignViewer"));
const ProductionExport = lazy(() => import("@/pages/ProductionExport"));
const DesignDNAGenerator = lazy(() => import("@/pages/DesignDNAGenerator"));
const RequestService = lazy(() => import("@/pages/marketplace/RequestService"));
const MultimodalRFQ = lazy(() => import("@/pages/marketplace/MultimodalRFQ"));
const Quotations = lazy(() => import("@/pages/marketplace/Quotations"));
const QuotationDetail = lazy(() => import("@/pages/marketplace/QuotationDetail"));
const RFQDetail = lazy(() => import("@/pages/marketplace/RFQDetail"));
const MyRFQs = lazy(() => import("@/pages/marketplace/MyRFQs"));
const TeamManagement = lazy(() => import("@/pages/marketplace/TeamManagement"));
const Orders = lazy(() => import("@/pages/marketplace/Orders"));
const Reports = lazy(() => import("@/pages/marketplace/Reports"));
const ProfileSettings = lazy(() => import("@/pages/marketplace/ProfileSettings"));
const HelpCenter = lazy(() => import("@/pages/marketplace/HelpCenter"));
const AdminApprovals = lazy(() => import("@/pages/marketplace/AdminApprovals"));
const AdminUsers = lazy(() => import("@/pages/marketplace/AdminUsers"));
const AdminCustomers = lazy(() => import("@/pages/marketplace/AdminCustomers"));
const AdminVendors = lazy(() => import("@/pages/marketplace/AdminVendors"));
const AdminPendingApprovals = lazy(() => import("@/pages/marketplace/AdminPendingApprovals"));
const AdminVendorVerification = lazy(() => import("@/pages/marketplace/AdminVendorVerification"));
const AdminRFQs = lazy(() => import("@/pages/marketplace/AdminRFQs"));
const AdminManagement = lazy(() => import("@/pages/marketplace/AdminManagement"));
const AdminDesignSettings = lazy(() => import("@/pages/marketplace/AdminDesignSettings"));
const AdminOrders = lazy(() => import("@/pages/marketplace/AdminOrders"));
const MessagesInbox = lazy(() => import("@/pages/marketplace/MessagesInbox"));
const PreFunctionalDesign = lazy(() => import("@/pages/PreFunctionalDesign"));
const AdminUserManagement = lazy(() => import("@/pages/marketplace/AdminUserManagement"));
const AdminRFQDetail = lazy(() => import("@/pages/marketplace/AdminRFQDetail"));
const AdminDocuments = lazy(() => import("@/pages/marketplace/AdminDocuments"));
const VendorDocuments = lazy(() => import("@/pages/marketplace/VendorDocuments"));
const CustomerQuestionnaires = lazy(() => import("@/pages/marketplace/CustomerQuestionnaires"));
const AIEventDesign = lazy(() => import("@/pages/marketplace/AIEventDesign"));
const OrderTracking = lazy(() => import("@/pages/marketplace/OrderTracking"));
const VendorProfile = lazy(() => import("@/pages/marketplace/VendorProfile"));
const CustomerProfile = lazy(() => import("@/pages/marketplace/CustomerProfile"));
const MyProfile = lazy(() => import("@/pages/marketplace/MyProfile"));
const AdminEmailNotifications = lazy(() => import("@/pages/marketplace/AdminEmailNotifications"));
const AdminNDASettings = lazy(() => import("@/pages/marketplace/AdminNDASettings"));
const AdminTermsConditions = lazy(() => import("@/pages/marketplace/AdminTermsConditions"));
const AdminManagedRFPs = lazy(() => import("@/pages/marketplace/AdminManagedRFPs"));
const AdminManagedQuotations = lazy(() => import("@/pages/marketplace/AdminManagedQuotations"));
const AdminManagedJobs = lazy(() => import("@/pages/marketplace/AdminManagedJobs"));
const BMManagedJobs = lazy(() => import("@/pages/marketplace/BMManagedJobs"));
const AdminManagedProjects = lazy(() => import("@/pages/marketplace/AdminManagedProjects"));
const BMManagedProjects = lazy(() => import("@/pages/marketplace/BMManagedProjects"));
const AdminV2Sourcing = lazy(() => import("@/pages/marketplace/AdminV2Sourcing"));
const AdminV2Offers = lazy(() => import("@/pages/marketplace/AdminV2Offers"));
const CustomerV2Offers = lazy(() => import("@/pages/marketplace/CustomerV2Offers"));
const VendorV2Invitations = lazy(() => import("@/pages/marketplace/VendorV2Invitations"));
const AdminMonitoring = lazy(() => import("@/pages/marketplace/AdminMonitoring"));
const Workflow = lazy(() => import("@/pages/marketplace/Workflow"));
const CalendarPage = lazy(() => import("@/pages/marketplace/CalendarPage"));
const Notifications = lazy(() => import("@/pages/marketplace/Notifications"));
const AIVisualDesign = lazy(() => import("@/pages/marketplace/AIVisualDesign"));
const AIStudyDesign = lazy(() => import("@/pages/marketplace/AIStudyDesign"));

function LazyFallback() {
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-400 text-sm">Loading...</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <SidebarProvider>
      <div className="App">
        <BrowserRouter>
          <Suspense fallback={<LazyFallback />}>
          <Routes>
            {/* AI Event Design Routes (Legacy) */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            <Route path="/event-brief" element={<EventBriefForm />} />
            <Route path="/design-generator/:eventId" element={<DesignGenerator />} />
            <Route path="/design-viewer/:designId" element={<DesignViewer />} />
            <Route path="/production/:designId" element={<ProductionExport />} />
            
            {/* Zero-Credit Design DNA Generator */}
            <Route path="/design-dna" element={<DesignDNAGenerator />} />

            {/* Marketplace Routes */}
            <Route path="/marketplace" element={<Navigate to="/marketplace/login" replace />} />
            <Route path="/marketplace/login" element={<MarketplaceLogin />} />
            <Route path="/marketplace/register" element={<MarketplaceRegister />} />
            <Route path="/marketplace/dashboard" element={<MarketplaceDashboard />} />
            <Route path="/marketplace/request-service" element={<RequestService />} />
            <Route path="/marketplace/multimodal-rfq" element={<MultimodalRFQ />} />
            <Route path="/marketplace/quotations" element={<Quotations />} />
            <Route path="/marketplace/quotations/:quotationId" element={<QuotationDetail />} />
            <Route path="/marketplace/rfqs" element={<Quotations />} />
            <Route path="/marketplace/rfq/:rfqId" element={<RFQDetail />} />
            <Route path="/marketplace/my-rfqs" element={<MyRFQs />} />
            <Route path="/marketplace/my-quotations" element={<Quotations />} />
            <Route path="/marketplace/team" element={<TeamManagement />} />
            <Route path="/marketplace/orders" element={<Orders />} />
            <Route path="/marketplace/orders/:orderId" element={<OrderTracking />} />
            <Route path="/marketplace/payments" element={<MarketplaceDashboard />} />
            <Route path="/marketplace/reports" element={<Reports />} />
            <Route path="/marketplace/notifications" element={<Notifications />} />
            <Route path="/marketplace/workflow" element={<Workflow />} />
            <Route path="/marketplace/calendar" element={<CalendarPage />} />
            <Route path="/marketplace/settings" element={<ProfileSettings />} />
            <Route path="/marketplace/profile" element={<ProfileSettings />} />
            <Route path="/marketplace/help" element={<HelpCenter />} />
            <Route path="/marketplace/contact" element={<Navigate to="/marketplace/help" replace />} />
            
            {/* Messages Routes */}
            <Route path="/marketplace/messages" element={<MessagesInbox />} />
            <Route path="/marketplace/messages/:conversationId" element={<MessagesInbox />} />
            
            {/* Admin Routes */}
            <Route path="/marketplace/approvals" element={<AdminApprovals />} />
            <Route path="/marketplace/users" element={<AdminUsers />} />
            <Route path="/marketplace/admin/customers" element={<AdminCustomers />} />
            <Route path="/marketplace/admin/vendors" element={<AdminVendors />} />
            <Route path="/marketplace/admin/pending-approvals" element={<AdminPendingApprovals />} />
            <Route path="/marketplace/admin/vendor-verification" element={<AdminVendorVerification />} />
            <Route path="/marketplace/admin/user-management" element={<AdminUserManagement />} />
            <Route path="/marketplace/admin/rfq/:rfqId" element={<AdminRFQDetail />} />
            <Route path="/marketplace/admin/rfqs" element={<AdminRFQs />} />
            <Route path="/marketplace/admin/orders" element={<AdminOrders />} />
            <Route path="/marketplace/admin/documents" element={<AdminDocuments />} />
            <Route path="/marketplace/admin/admins" element={<AdminManagement />} />
            <Route path="/marketplace/admin/design-settings" element={<AdminDesignSettings />} />
            <Route path="/marketplace/admin/email-notifications" element={<AdminEmailNotifications />} />
            <Route path="/marketplace/admin/nda-settings" element={<AdminNDASettings />} />
            <Route path="/marketplace/admin/terms-conditions" element={<AdminTermsConditions />} />
            <Route path="/marketplace/admin/managed-rfps" element={<AdminManagedRFPs />} />
            <Route path="/marketplace/admin/managed-rfq/:rfqId/quotations" element={<AdminManagedQuotations />} />
            <Route path="/marketplace/admin/managed-jobs" element={<AdminManagedJobs />} />
            <Route path="/marketplace/managed-jobs" element={<BMManagedJobs />} />
            <Route path="/marketplace/admin/managed-projects" element={<AdminManagedProjects />} />
            <Route path="/marketplace/managed-projects" element={<BMManagedProjects />} />
            <Route path="/marketplace/admin/v2-sourcing" element={<AdminV2Sourcing />} />
            <Route path="/marketplace/admin/v2-offers" element={<AdminV2Offers />} />
            <Route path="/marketplace/v2-proposals" element={<CustomerV2Offers />} />
            <Route path="/marketplace/v2-invitations" element={<VendorV2Invitations />} />
            <Route path="/marketplace/admin/monitoring" element={<AdminMonitoring />} />
            <Route path="/marketplace/all-rfqs" element={<AdminRFQs />} />
            <Route path="/marketplace/all-orders" element={<Orders />} />
            
            {/* AI Design Integration */}
            <Route path="/marketplace/ai-design" element={<AIEventDesign />} />
            <Route path="/marketplace/prefunctional-design" element={<PreFunctionalDesign />} />
            <Route path="/marketplace/ai-visual-design" element={<AIVisualDesign />} />
            <Route path="/marketplace/ai-study-design" element={<AIStudyDesign />} />
            
            {/* Document Management Routes */}
            <Route path="/marketplace/vendor/documents" element={<VendorDocuments />} />
            <Route path="/marketplace/customer/questionnaires" element={<CustomerQuestionnaires />} />
            
            {/* Profile Pages */}
            <Route path="/marketplace/vendor/:vendorId" element={<VendorProfile />} />
            <Route path="/marketplace/customer/:customerId" element={<CustomerProfile />} />
            <Route path="/marketplace/my-profile" element={<MyProfile />} />
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          <MobileBottomNav />
          <PushAutoSubscribe />
        </BrowserRouter>
        <Toaster />
      </div>
      </SidebarProvider>
    </AuthProvider>
  );
}

export default App;
